/**
 * Change history, as shown to the super admin (docs/phase-5-plan.md).
 * The entries are written by the `recordListingHistory` Cloud Function
 * (functions/src/historyDiff.ts makes the list of changes). Pure helpers, no
 * Firebase, no i18n.
 */
import en from '../i18n/en.json';

export interface HistoryFieldChange {
  /** e.g. 'nameHe' or 'location.addressHe'. */
  field: string;
  /** null = the field was not there. */
  before: unknown;
  after: unknown;
  /** True when the values were too big to keep. */
  valuesOmitted?: boolean;
}

export interface HistoryEntry {
  id: string;
  /** ISO time of the change. */
  at: string;
  action: 'create' | 'update' | 'delete';
  /** uid of the person, or null (script / unknown). */
  by: string | null;
  byScript: boolean;
  changes: HistoryFieldChange[];
}

/** Newest first. */
export function sortHistory(entries: HistoryEntry[]): HistoryEntry[] {
  return [...entries].sort((a, b) => b.at.localeCompare(a.at));
}

/** Fields never put back: they describe the listing record itself. */
const NOT_PUT_BACK = ['createdAt', 'schemaVersion'];

function canPutBackChange(c: HistoryFieldChange): boolean {
  return !c.valuesOmitted && !NOT_PUT_BACK.includes(c.field);
}

/** Only an edit can be put back (not the creation or deletion of a listing). */
export function canPutBack(entry: HistoryEntry): boolean {
  return entry.action === 'update' && entry.changes.some(canPutBackChange);
}

/**
 * The save that sets the changed fields back to how they were before this
 * change. `undefined` = remove the field (it wasn't there before); the
 * updateListing hook turns that into a Firestore delete.
 */
export function putBackPatch(entry: HistoryEntry): Record<string, unknown> {
  if (!canPutBack(entry)) return {};
  return Object.fromEntries(
    entry.changes.filter(canPutBackChange).map((c) => [c.field, c.before === null ? undefined : c.before])
  );
}

// ---------- How a change is shown ----------

/** Fields with their own label under directory.history.field. */
const HISTORY_FIELD_KEYS = Object.keys(en.directory.history.field);
/** Fields labelled in directory.field (shared with the edit forms). */
const SHARED_FIELD_KEYS = Object.keys(en.directory.field);

export interface FieldLabel {
  /** Translation key. */
  key: string;
  /** For bilingual fields whose label has no language in it (e.g. details.feeNoteHe). */
  lang?: 'en' | 'he';
  /** Fallback when no label exists: the raw field path. */
  raw?: string;
}

/** Which label to show for a field path, e.g. 'location.areaId' → directory.field.area. */
export function historyFieldLabel(path: string): FieldLabel {
  const name = path.includes('.') ? path.slice(path.indexOf('.') + 1) : path;
  const leaf = name === 'areaId' ? 'area' : name;
  if (!path.includes('.') && HISTORY_FIELD_KEYS.includes(leaf)) return { key: `directory.history.field.${leaf}` };
  if (SHARED_FIELD_KEYS.includes(leaf)) return { key: `directory.field.${leaf}` };
  const match = /^(.*)(En|He)$/.exec(leaf);
  if (match && SHARED_FIELD_KEYS.includes(match[1])) {
    return { key: `directory.field.${match[1]}`, lang: match[2] === 'En' ? 'en' : 'he' };
  }
  return { key: '', raw: path };
}

export type ValueSummary =
  | { kind: 'empty' }
  | { kind: 'text'; text: string }
  | { kind: 'yesNo'; value: boolean }
  | { kind: 'items'; count: number }
  | { kind: 'changedData' };

const MAX_TEXT = 200;

/** A short, readable form of an old or new value. */
export function summarizeValue(value: unknown): ValueSummary {
  if (value === null || value === undefined || value === '') return { kind: 'empty' };
  if (typeof value === 'boolean') return { kind: 'yesNo', value };
  if (typeof value === 'string' || typeof value === 'number') {
    const text = String(value);
    return { kind: 'text', text: text.length > MAX_TEXT ? `${text.slice(0, MAX_TEXT)}…` : text };
  }
  if (Array.isArray(value)) {
    if (value.length === 0) return { kind: 'empty' };
    if (value.every((v) => typeof v === 'string' || typeof v === 'number')) {
      return summarizeValue(value.join(', '));
    }
    return { kind: 'items', count: value.length };
  }
  return { kind: 'changedData' };
}
